import { Collapse, Drawer, Popover, Tooltip } from "@mui/material";
import { getFromLocalStorage } from "helpers/localstorage";
import { useLanguage } from "locale/useLanguage";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "assets/images/logo-2.png";
import logoNikon from "assets/images/logo-Nikon.png";
import { NavIcon } from "./icons";
import MenuSearch, { shortcutLabel } from "./MenuSearch";
import useNavigation, { isActivePath, NavItem } from "./useNavigation";
import "./Sidebar.scss";

const STORAGE_KEY = "sidebar-collapsed";
export const SIDEBAR_WIDTH = 252;
export const SIDEBAR_COLLAPSED_WIDTH = 72;

const readCollapsed = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

const isItemActive = (pathname: string, item: NavItem) =>
  item.match.some((path) => isActivePath(pathname, path));

type SidebarProps = {
  /** Drawer a scomparsa sotto i 900px, aperto dal pulsante nell'header */
  mobileOpen: boolean;
  onMobileClose: () => void;
};

function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [searchOpen, setSearchOpen] = useState(false);
  const { groups, families } = useNavigation();

  const toggleCollapsed = useCallback(() => {
    setCollapsed((value) => {
      try {
        localStorage.setItem(STORAGE_KEY, value ? "0" : "1");
      } catch {
        // preferenza non salvata: il menu funziona comunque
      }
      return !value;
    });
  }, []);

  // L'header (fisso) usa questa variabile per lasciare spazio alla sidebar.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--sidebar-w", `${collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH}px`);
    return () => {
      root.style.removeProperty("--sidebar-w");
    };
  }, [collapsed]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const openSearch = () => {
    onMobileClose();
    setSearchOpen(true);
  };

  return (
    <>
      <aside
        className={`sidebar sidebar--desktop ${collapsed ? "is-collapsed" : ""}`}
        style={{ width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH }}
      >
        <SidebarContent
          collapsed={collapsed}
          groups={groups}
          onToggle={toggleCollapsed}
          onSearch={openSearch}
        />
      </aside>

      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={onMobileClose}
        className="sidebar-drawer"
        PaperProps={{ className: "sidebar sidebar--mobile", style: { width: 280 } }}
      >
        <SidebarContent
          collapsed={false}
          groups={groups}
          onClose={onMobileClose}
          onNavigate={onMobileClose}
          onSearch={openSearch}
        />
      </Drawer>

      <MenuSearch
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        groups={groups}
        families={families}
      />
    </>
  );
}

type SidebarContentProps = {
  collapsed: boolean;
  groups: ReturnType<typeof useNavigation>["groups"];
  onToggle?: () => void;
  onClose?: () => void;
  onNavigate?: () => void;
  onSearch: () => void;
};

function SidebarContent({ collapsed, groups, onToggle, onClose, onNavigate, onSearch }: SidebarContentProps) {
  const [t] = useLanguage();
  const { pathname } = useLocation();
  const { agencyName } = (getFromLocalStorage() as any) ?? {};
  const isNikon = ["nikon", "nital"].includes(String(agencyName).toLowerCase());

  return (
    <div className="sidebar__inner">
      <div className="sidebar__top">
        <Link to="/" className="sidebar__logo" aria-label="Noleggio su Misura – Home" onClick={onNavigate}>
          {collapsed && !isNikon ? (
            <span className="sidebar__logo-mark">
              <img src={logo} alt="" />
            </span>
          ) : (
            <img src={isNikon ? logoNikon : logo} alt="Noleggio su Misura" className="sidebar__logo-full" />
          )}
        </Link>
        {onToggle && (
          <Tooltip title={collapsed ? t("navigation.expand") : t("navigation.collapse")} placement="right">
            <button
              type="button"
              className="sidebar__icon-btn"
              onClick={onToggle}
              aria-label={collapsed ? t("navigation.expand") : t("navigation.collapse")}
              aria-expanded={!collapsed}
            >
              <NavIcon name="panel" style={{ transform: collapsed ? "scaleX(-1)" : undefined }} />
            </button>
          </Tooltip>
        )}
        {onClose && (
          <button type="button" className="sidebar__icon-btn" onClick={onClose} aria-label={t("navigation.close_menu")}>
            <NavIcon name="close" />
          </button>
        )}
      </div>

      {collapsed ? (
        <Tooltip title={`${t("navigation.search.button")} · ${shortcutLabel}`} placement="right">
          <button type="button" className="sidebar__rail-btn" onClick={onSearch} aria-label={t("navigation.search.button")}>
            <NavIcon name="search" />
          </button>
        </Tooltip>
      ) : (
        <button type="button" className="sidebar__search" onClick={onSearch}>
          <NavIcon name="search" size={15} />
          <span className="sidebar__search-label">{t("navigation.search.button")}</span>
          <kbd>{shortcutLabel}</kbd>
        </button>
      )}

      <nav className="sidebar__nav" aria-label={t("navigation.label")}>
        {groups.map((group, index) => (
          <Fragment key={group.id}>
            {collapsed ? (
              index > 0 && <span className="sidebar__rail-sep" aria-hidden="true" />
            ) : (
              <span className="sidebar__group">{group.title}</span>
            )}
            {group.items.map((item) =>
              collapsed ? (
                <RailItem key={item.id} item={item} active={isItemActive(pathname, item)} pathname={pathname} />
              ) : (
                <ExpandedItem
                  key={item.id}
                  item={item}
                  active={isItemActive(pathname, item)}
                  pathname={pathname}
                  onNavigate={onNavigate}
                />
              ),
            )}
          </Fragment>
        ))}
      </nav>
    </div>
  );
}

type ItemProps = { item: NavItem; active: boolean; pathname: string; onNavigate?: () => void };

function ExpandedItem({ item, active, pathname, onNavigate }: ItemProps) {
  const [open, setOpen] = useState(active);

  useEffect(() => {
    if (active) setOpen(true);
  }, [active]);

  if (!item.expandable || !item.children) {
    return (
      <Link
        to={item.path}
        className={`sidebar__item ${active ? "is-active" : ""}`}
        aria-current={active ? "page" : undefined}
        onClick={onNavigate}
      >
        <NavIcon name={item.icon} />
        <span className="sidebar__label">{item.title}</span>
        {item.children && <NavIcon name="chevronRight" size={15} className="sidebar__chev" />}
      </Link>
    );
  }

  const submenuId = `sidebar-sub-${item.id}`;
  return (
    <>
      <button
        type="button"
        className={`sidebar__item ${active ? "is-parent-active" : ""}`}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls={submenuId}
      >
        <NavIcon name={item.icon} />
        <span className="sidebar__label">{item.title}</span>
        <NavIcon
          name="chevronDown"
          size={15}
          className="sidebar__chev"
          style={{ transform: open ? "rotate(180deg)" : undefined }}
        />
      </button>
      <Collapse in={open} timeout={200} id={submenuId}>
        <div className="sidebar__sub">
          {item.children.map((child) => {
            const childActive = isActivePath(pathname, child.path);
            return (
              <Link
                key={child.path}
                to={child.path}
                className={`sidebar__item sidebar__item--sub ${childActive ? "is-active" : ""}`}
                aria-current={childActive ? "page" : undefined}
                onClick={onNavigate}
              >
                {child.title}
              </Link>
            );
          })}
        </div>
      </Collapse>
    </>
  );
}

function RailItem({ item, active, pathname }: ItemProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const flyoutId = `sidebar-flyout-${item.id}`;
  const hasFlyout = useMemo(() => !!item.expandable && !!item.children?.length, [item]);

  if (!hasFlyout) {
    return (
      <Tooltip title={item.title} placement="right">
        <Link
          to={item.path}
          className={`sidebar__rail-btn ${active ? "is-active" : ""}`}
          aria-label={item.title}
          aria-current={active ? "page" : undefined}
        >
          <NavIcon name={item.icon} />
        </Link>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip title={anchor ? "" : item.title} placement="right">
        <button
          type="button"
          className={`sidebar__rail-btn ${active ? "is-active" : ""}`}
          aria-label={item.title}
          aria-haspopup="menu"
          aria-expanded={!!anchor}
          aria-controls={anchor ? flyoutId : undefined}
          onClick={(e) => setAnchor(e.currentTarget)}
        >
          <NavIcon name={item.icon} />
        </button>
      </Tooltip>
      <Popover
        id={flyoutId}
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: "sidebar-flyout", style: { marginLeft: 12 } }}
      >
        <span className="sidebar__group sidebar__group--flyout">{item.title}</span>
        <div role="menu" aria-label={item.title}>
          {item.children?.map((child) => {
            const childActive = isActivePath(pathname, child.path);
            return (
              <Link
                key={child.path}
                to={child.path}
                role="menuitem"
                className={`sidebar__item ${childActive ? "is-active" : ""}`}
                aria-current={childActive ? "page" : undefined}
                onClick={() => setAnchor(null)}
              >
                {child.title}
              </Link>
            );
          })}
        </div>
      </Popover>
    </>
  );
}

export default Sidebar;
